<template>
  <v-dialog :value="isOpen" width="600" @input="$emit('closed')">
    <v-card>
      <v-card-title class="subheading font-weight-bold px-4">
        <span>Edit Aid  - {{ event.title }}</span>
        <v-spacer></v-spacer>
        <v-icon @click="$emit('closed')">close</v-icon>
      </v-card-title>

      <v-divider></v-divider>
      <v-form ref="form" @submit.prevent="doEditEvent">
        <v-container class="px-4">
          <app-alert
            class="mb-3"
            type="error"
            :message="error"
            :outline="true"
            @dismissed="error = false"
          />
          <v-layout justify-space-between row wrap>
            <v-flex xs12 v-if="canEditEventStatus">
              <v-select
                label="Status"
                v-model="event.status"
                @change="canSave = true"
                hint="Has this event been verified or not?"
                persistent-hint
                :items="statusOptions"
                :rules="[rules.isRequired]"
                :disabled="isLoading"
                required
              ></v-select>
            </v-flex>

            <v-flex xs12>
              <v-text-field
                label="Event Title"
                type="text"
                v-model="event.title"
                @input="canSave = true"
                hint="A title of the Event"
                persistent-hint
                :rules="[rules.isRequired]"
                required
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-textarea
                label="Event Description"
                type="text"
                v-model="event.description"
                @input="canSave = true"
                hint="A description of the Event"
                persistent-hint
								rows="2"
                :rules="[rules.isRequired]"
                required
              ></v-textarea>
            </v-flex>

            <v-flex xs12 sm6>
              <v-select
                label="State"
                v-model="event.state_id"
                @change="canSave = true"
                hint="The State where this event occured"
                persistent-hint
                :items="stateOptions"
                :loading="isLoading"
                :disabled="isLoading"
                :rules="[rules.isRequired]"
                required
              ></v-select>
            </v-flex>

            <v-flex xs12 sm6>
              <v-autocomplete
                label="Location"
                v-model="event.location_id"
                @change="canSave = true"
                hint="The location where this event occured"
                persistent-hint
                :items="locationOptions"
                :rules="[rules.isRequired]"
                :loading="isLoading"
                :disabled="isLoading"
                no-data-text="No locations in selected state"
                required
              ></v-autocomplete>
            </v-flex>

            <v-flex xs12 sm6>
              <v-autocomplete
                label="Partner"
                v-model="event.agency_id"
                @change="canSave = true"
                hint="The partner name"
                persistent-hint
                :items="agencyOptions"
                :loading="isLoading"
                :disabled="isLoading"
                :rules="[rules.isRequired]"
                required
              ></v-autocomplete>
            </v-flex>

            <v-flex xs12 sm6>
              <v-text-field
                label="Affected Population"
                type="number"
                v-model="event.affected"
                @input="canSave = true"
                hint="An estimation of population affected"
                persistent-hint
                :rules="[rules.isRequired]"
                required
              ></v-text-field>
            </v-flex>

            <!-- Date Picker -->
            <v-flex xs12 sm6>
              <v-dialog
                ref="dateDialog"
                v-model="dateModal"
                :return-value.sync="event.timestamp"
                persistent
                lazy
                full-width
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    label="Date"
                    v-model="event.timestamp"
                    hint="The date of the Event"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="event.timestamp" :allowed-dates="allowedDates" scrollable>
                  <v-spacer></v-spacer>
                  <v-btn flat color="primary" @click="dateModal = false">Cancel</v-btn>
                  <v-btn flat color="primary" @click="updateDate(event.timestamp)">OK</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-flex>
          </v-layout>

          <v-layout class="mt-4 mb-2">
            <v-btn depressed color="accent" type="submit" :disabled="isSaving || !canSave">
              Save
              <v-progress-circular
                class="ml-3"
                :indeterminate="true"
                :size="18"
                :width="2"
                v-if="isSaving"
              ></v-progress-circular>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn depressed @click="$emit('closed')">Cancel</v-btn>
          </v-layout>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { EVENT_UPDATE } from '@/store/actions/event'
import { LOCATION_FETCH } from '@/store/actions/location'

import { eventStatusOptions } from '@/data/options'
import rolesData from '@/data/roles'

export default {
	name: 'EditEvent',
  props: ['isOpen', 'eventData'],
  created () {
    this.isLoading = true
    Promise.all([
      this.$store.dispatch(LOCATION_FETCH)
    ]).then(() => {
      this.isLoading = false
    })
  },
	data () {
		return {
			event: {
				_id: this.eventData._id,
				status: this.eventData.status,  
				tag: '-',    
				timestamp: (new Date(this.eventData.timestamp * 1000)).toISOString().split('T')[0],
				title: this.eventData.title,
				description: this.eventData.description,
				affected: this.eventData.affected,
				agent_id: this.eventData.agent_id,
				location_id: this.eventData.location_id,
				state_id: this.eventData.state_id,
				agency_id: this.eventData.agency_id,
				country_id: this.eventData.country_id
			},
			canSave: false,
			isSaving: false,
			isLoading: false,
      statusOptions: eventStatusOptions,
      dateModal: false,
			error: false,
      rules: {
        isRequired: value => this.$validator.isRequired(value)
      }
		}
	},
	computed: {
		stateOptions () {
      let stateOptions = this.$store.getters.statesAsOptions
      // If state agent, show only agents state in list
      if (this.currentUser.parent && this.currentUser.parent === 'state') {
        const state = stateOptions.find(stateOption => this.currentUser.parent_id === stateOption.value)
        stateOptions = [state]
      }
      return stateOptions
    },
    locationOptions () {
      return this.$store.getters.locationsAsOptions(this.event.state_id)
    },
    agencyOptions () {
      let agencyOptions = this.$store.getters.agenciesAsOptions
      // If agency agent, show only agents agency in list
      if (this.currentUser.parent && this.currentUser.parent === 'agency') {
        const agency = agencyOptions.find(agencyOption => this.currentUser.parent_id === agencyOption.value)
        agencyOptions = [agency]
      }
      return agencyOptions
    },
    /**
     * Permission for who can edit the status of an aid
     */
    canEditEventStatus () {
      return this.currentUser.role === rolesData.stateHead
    },
    ...mapGetters(['currentUser'])
	},
	methods: {
		allowedDates (date) {
      return new Date(date) <= new Date()
    },
    updateDate (date) {
			this.canSave = true
			this.$refs.dateDialog.save(date)
		},
		doEditEvent () {
      if (!this.$refs.form.validate()) return

      this.isSaving = true
      this.error = false

      const event = Object.assign({}, this.event)
      event.timestamp = Math.round((new Date(this.event.timestamp)).getTime() / 1000)

      this.$store.dispatch(EVENT_UPDATE, event)
        .then((aid) => {
          this.$emit('saved', event)
          this.$emit('closed')
        })
        .catch(error => {
          this.error = error.message
        })
        .finally(() => {
          this.canSave = false
          this.isSaving = false
        })
		}
	}
}
</script>
